import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { LossReasonChips } from "@/lib/lossReasons";

// Generic confirm dialog for a bulk action: optional note and/or day count.
export function BulkActionDialog({ open, onOpenChange, config, count, onConfirm }) {
  const [note, setNote] = useState("");
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setNote(""); setDays(config?.days || 7); } }, [open, config]);
  if (!config) return null;

  const canSubmit = !(config.noteRequired && !note.trim());
  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try { await onConfirm({ note: note.trim(), days: Number(days) || 7 }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-action-dialog">
        <DialogHeader>
          <DialogTitle>{config.title} · {count} bid{count === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        {config.description && <p className="text-sm text-slate-500 -mt-1">{config.description}</p>}
        {config.needsDays && (
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-slate-400">Snooze for (days)</label>
            <Input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} data-testid="bulk-days-input" />
          </div>
        )}
        {config.needsNote && (
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-slate-400">
              {config.noteLabel || "Note"}{config.noteRequired ? " *" : " (optional)"}
            </label>
            {config.presets && <LossReasonChips value={note} onPick={setNote} testidPrefix="bulk-loss-reason" />}
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={config.notePlaceholder} data-testid="bulk-note-input" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !canSubmit} data-testid="bulk-confirm"
            className={config.tone === "danger" ? "bg-rose-600 hover:bg-rose-500 text-white" : "bg-[#003E87] hover:bg-[#00305F] text-white"}>
            {config.confirmLabel || "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
